/**
 * 机上調査の調査の切り替え (docs/98 R-97) の管理画面側の部品: URL で選ぶ調査と、調査ごとの設定の保存。
 *
 * - 選んだ調査は URL の `?survey=<key>` に持つ (詳細レポートを別タブで開いても同じ調査になる)。
 *   地区に無い key は既定の調査にする。
 * - 既定の調査の設定は従来どおり `district.aiAnalysis` の `streetSurveyTemplateId` / `streetSurveyOverride`。
 *   追加の調査の設定は `district.aiAnalysis.streetSurveys[]` の該当の要素 (配列ごと書き直す)。
 */
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_STREET_SURVEY_KEY,
  getModeLabel,
  isDefaultStreetSurvey,
  listAdditionalStreetSurveys,
  listStreetSurveyOptions,
  type District,
  type StreetSurveyDisplayDef,
  type StreetSurveyOption,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export const STREET_SURVEY_QUERY_PARAM = 'survey';

/** 既定の調査の表示名 (モードの表示名 → 「既定の調査」)。内部のキーは出さない。 */
export function defaultStreetSurveyLabel(district: District | null | undefined): string {
  if (!district?.mode) return '既定の調査';
  const label = getModeLabel(district.mode, district.modeLabel);
  return label && label !== district.mode ? label : '既定の調査';
}

export interface StreetSurveySelection {
  surveyKey: string;
  isDefault: boolean;
  options: StreetSurveyOption[];
  setSurveyKey: (key: string) => void;
  /** 詳細レポートなどへのリンクに付ける検索文字列 (既定の調査なら空)。 */
  querySuffix: string;
}

/** URL の `?survey=` で選んだ調査 (地区に無い key は既定の調査)。 */
export function useStreetSurveySelection(
  district: District | null | undefined,
): StreetSurveySelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(STREET_SURVEY_QUERY_PARAM);
  const options = useMemo(
    () => listStreetSurveyOptions(district?.aiAnalysis, defaultStreetSurveyLabel(district)),
    [district],
  );
  const surveyKey =
    requested && options.some((o) => o.key === requested) ? requested : DEFAULT_STREET_SURVEY_KEY;
  const isDefault = isDefaultStreetSurvey(surveyKey);
  return {
    surveyKey,
    isDefault,
    options,
    querySuffix: isDefault ? '' : `?${STREET_SURVEY_QUERY_PARAM}=${encodeURIComponent(surveyKey)}`,
    setSurveyKey: (key) => {
      const next = new URLSearchParams(searchParams);
      if (isDefaultStreetSurvey(key)) next.delete(STREET_SURVEY_QUERY_PARAM);
      else next.set(STREET_SURVEY_QUERY_PARAM, key);
      setSearchParams(next, { replace: true });
    },
  };
}

export interface StreetSurveySettingsPatch {
  /** 適用する机上調査テンプレート (undefined = 変えない、null = 外す)。 */
  templateId?: string | null;
  /** 机上調査の上書きのうち書き換える項目 (項目単位で置き換える)。 */
  override?: Partial<StreetSurveyDisplayDef>;
}

/**
 * 調査の設定を保存する。既定の調査は `aiAnalysis` に merge、追加の調査は `streetSurveys` を配列ごと書き直す
 * (配列の要素は Firestore の merge で部分更新できないため)。
 */
export async function saveStreetSurveySettings(input: {
  districtId: string;
  district: District | null;
  surveyKey: string;
  patch: StreetSurveySettingsPatch;
  uid: string;
}): Promise<void> {
  const { districtId, district, surveyKey, patch, uid } = input;
  let aiAnalysis: Record<string, unknown>;
  if (isDefaultStreetSurvey(surveyKey)) {
    aiAnalysis = {
      ...(patch.templateId !== undefined ? { streetSurveyTemplateId: patch.templateId } : {}),
      ...(patch.override ? { streetSurveyOverride: patch.override } : {}),
    };
  } else {
    const surveys = listAdditionalStreetSurveys(district?.aiAnalysis);
    if (!surveys.some((s) => s.key === surveyKey)) {
      throw new Error('調査が見つかりません。画面を読み込み直してください。');
    }
    aiAnalysis = {
      streetSurveys: surveys.map((s) =>
        s.key === surveyKey
          ? {
              ...s,
              ...(patch.templateId !== undefined
                ? { streetSurveyTemplateId: patch.templateId }
                : {}),
              ...(patch.override
                ? { streetSurveyOverride: { ...(s.streetSurveyOverride ?? {}), ...patch.override } }
                : {}),
            }
          : s,
      ),
    };
  }
  await setDoc(
    doc(db, 'districts', districtId),
    { aiAnalysis, updatedAt: serverTimestamp(), updatedBy: uid },
    { merge: true },
  );
}
